import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { apiData } from "../services/api_service";
import { reservedStockDetailsAPI } from "../config";

// Drill-down for the "Reserved" figure on the Stock Inventory Report: lists
// every active CDN (Consignment Delivery Note) currently holding reserved
// stock for a given product + store, and the salesman if the CDN was raised
// against one. A CDN with no salesman shows "—" (goods held generically in
// the store, not by a specific person).
const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return { bg: "#fdecea", color: "#c62828" };
    case "converted":
      return { bg: "#e8f5e9", color: "#2e7d32" };
    case "fully returned":
    case "cancelled":
      return { bg: "#e3f2fd", color: "#1565c0" };
    default:
      return { bg: "#f5f5f5", color: "#616161" };
  }
};

// A CDN only has a viewable receipt once its PDF has actually been generated
// and uploaded (pdf_url set to a real value).
const hasReceipt = (r) =>
  r?.pdf_url && r.pdf_url !== "NA" && String(r.pdf_url).trim() !== "";

const ReservedStockDetailsModal = ({
  open,
  onClose,
  productId,
  storeId,
  productName,
  storeName,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // storeId is optional: pass it to scope to one store (Store-wise report),
  // omit it to see reservations across every store for the product
  // (Product-wise report) — each row's own store_name tells them apart then.
  const isMultiStore = !storeId;

  useEffect(() => {
    if (!open || !productId) return;
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const url = storeId
          ? `${reservedStockDetailsAPI}?product_id=${productId}&store_id=${storeId}`
          : `${reservedStockDetailsAPI}?product_id=${productId}`;
        const response = await apiData("get", url);
        setRows(response.success && Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        console.error("Failed to fetch reserved stock details:", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [open, productId, storeId]);

  const totalReserved = rows.reduce((sum, r) => sum + Number(r.quantity || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reserved Stock Detail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {productName || "Product"} {storeName ? `· ${storeName}` : ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            No active reservations for this product{storeId ? " at this store" : ""}.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>CDN No</TableCell>
                  {isMultiStore && (
                    <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Salesman</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Qty Reserved
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>CDN Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const sc = statusColor(r.cdn_status);
                  return (
                    <TableRow key={r.reservation_id}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {hasReceipt(r) ? (
                          <a
                            href={r.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              fontWeight: 600,
                              textDecoration: "none",
                            }}
                          >
                            {r.cdn_no || `#${r.proforma_id}`}
                          </a>
                        ) : (
                          r.cdn_no || `#${r.proforma_id}`
                        )}
                      </TableCell>
                      {isMultiStore && (
                        <TableCell>{r.store_name || "—"}</TableCell>
                      )}
                      <TableCell>
                        {r.order_date ? moment(r.order_date).format("DD-MM-YYYY") : "—"}
                      </TableCell>
                      <TableCell>{r.customer_name || "—"}</TableCell>
                      <TableCell>{r.salesman_name || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {Number(r.quantity || 0)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(r.cdn_status || "unknown").toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: sc.bg,
                            color: sc.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    colSpan={isMultiStore ? 5 : 4}
                    align="right"
                    sx={{ fontWeight: 600 }}
                  >
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {totalReserved}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReservedStockDetailsModal;
